"use client"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { CONTACT } from "@/lib/constants"
import { serviceTypes } from "@/lib/form-options"
import { complaintSchema, type ComplaintFormData } from "@/lib/validations/complaint"
import { zodResolver } from "@hookform/resolvers/zod"
import { motion } from "framer-motion"
import { AlertCircle, CheckCircle2, Clock, FileText, Loader2, Mail, MessageSquare, Phone, Send, Upload, X } from "lucide-react"
import { useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"

export function ComplaintForm() {
  const [submitted, setSubmitted] = useState(false)
  const [complaintId, setComplaintId] = useState("")
  const [submitError, setSubmitError] = useState("")

  const form = useForm<ComplaintFormData>({
    resolver: zodResolver(complaintSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      serviceType: "",
      bookingId: "",
      issue: "",
      supportingDocument: undefined,
    },
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  const onSubmit = async (formData: ComplaintFormData) => {
    try {
      setSubmitError("")

      // Create FormData for multipart upload
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.name)
      formDataToSend.append('phone', formData.phone)
      formDataToSend.append('email', formData.email)
      formDataToSend.append('serviceType', formData.serviceType)
      if (formData.bookingId) {
        formDataToSend.append('bookingId', formData.bookingId)
      }
      formDataToSend.append('issue', formData.issue)
      
      if (formData.supportingDocument) {
        formDataToSend.append('supportingDocument', formData.supportingDocument)
      }

      // Submit with fetch (browser will set Content-Type with boundary)
      const response = await fetch('/api/complaint', {
        method: 'POST',
        body: formDataToSend,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to submit complaint')
      }

      const data = await response.json()

      setComplaintId(data.id?.substring(0, 8).toUpperCase() || 'PENDING')
      setSubmitted(true)

      // Reset form after 8 seconds
      setTimeout(() => {
        setSubmitted(false)
        setComplaintId("")
        form.reset()
      }, 8000)
    } catch (error) {
      console.error("Error submitting complaint:", error)
      setSubmitError(
        error instanceof Error ? error.message : "Failed to submit complaint. Please try again."
      )
    }
  }

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  }

  return (
    <Card className="relative p-8 sm:p-10 bg-white border-2 border-slate-200 shadow-2xl rounded-3xl overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100 to-transparent rounded-full blur-3xl opacity-30 -z-0"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-orange-100 to-transparent rounded-full blur-3xl opacity-30 -z-0"></div>

      <div className="relative z-10">
        {submitted ? (
          <motion.div
            className="flex flex-col items-center justify-center py-16 text-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-6 shadow-lg">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Complaint Registered Successfully!
            </h3>
            <p className="text-muted-foreground text-lg mb-6 max-w-md">
              Your complaint ID: <span className="font-semibold text-[#003DA5]">#{complaintId}</span>
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-6 max-w-lg">
              <div className="flex items-start gap-3 mb-4">
                <Clock className="w-5 h-5 text-[#003DA5] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  Our support team will contact you within <span className="font-semibold">2 hours</span>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#FF7A00] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  A confirmation email has been sent to <span className="font-semibold">{form.getValues("email")}</span>
                </p>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
            variants={container}
            initial="hidden"
            animate="visible"
          >
            {/* Form Header */}
            <motion.div variants={item} className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#003DA5] to-blue-700 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-foreground">Complaint Details</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Please fill in all required fields. We'll get back to you within 2 hours.
              </p>
            </motion.div>

            {/* Personal Information */}
            <motion.div variants={item} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    {...form.register("name")}
                    className="h-12 border-2 focus:border-[#003DA5] transition-colors"
                  />
                  {form.formState.errors.name && (
                    <p className="text-xs text-red-600 mt-1">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={CONTACT.phone.display}
                    {...form.register("phone")}
                    className="h-12 border-2 focus:border-[#003DA5] transition-colors"
                  />
                  {form.formState.errors.phone && (
                    <p className="text-xs text-red-600 mt-1">{form.formState.errors.phone.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  {...form.register("email")}
                  className="h-12 border-2 focus:border-[#003DA5] transition-colors"
                />
                {form.formState.errors.email && (
                  <p className="text-xs text-red-600 mt-1">{form.formState.errors.email.message}</p>
                )}
              </div>
            </motion.div>

            {/* Service Details */}
            <motion.div variants={item} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Controller
                  name="serviceType"
                  control={form.control}
                  render={({ field }) => (
                    <div>
                      <Label htmlFor="serviceType" className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                        Service Type <span className="text-red-500">*</span>
                      </Label>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="serviceType" className="h-12 border-2 focus:border-[#003DA5]" aria-label="Select service type">
                          <SelectValue placeholder="Select service type" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.serviceType && (
                        <p className="text-xs text-red-600 mt-1">{form.formState.errors.serviceType.message}</p>
                      )}
                    </div>
                  )}
                />

                <div>
                  <Label htmlFor="bookingId" className="text-sm font-semibold text-foreground mb-2">
                    Booking ID <span className="text-muted-foreground text-xs">(Optional)</span>
                  </Label>
                  <Input
                    id="bookingId"
                    type="text"
                    placeholder="BW123456"
                    {...form.register("bookingId")}
                    className="h-12 border-2 focus:border-[#003DA5] transition-colors"
                  />
                </div>
              </div>
            </motion.div>

            {/* Issue Description */}
            <motion.div variants={item}>
              <Label htmlFor="issue" className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                Describe Your Issue <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="issue"
                placeholder="Please provide detailed information about your complaint. Include dates, locations, and any relevant details that will help us resolve your concern quickly."
                {...form.register("issue")}
                rows={6}
                className="resize-none border-2 focus:border-[#003DA5] transition-colors"
              />
              {form.formState.errors.issue && (
                <p className="text-xs text-red-600 mt-1">{form.formState.errors.issue.message}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                {form.watch("issue")?.length || 0}/500 characters
              </p>
            </motion.div>

            {/* File Upload */}
            <motion.div variants={item}>
              <Controller
                name="supportingDocument"
                control={form.control}
                render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                  <>
                    <Label className="text-sm font-semibold text-foreground mb-2 block">
                      Attach Supporting Documents <span className="text-muted-foreground text-xs">(Optional)</span>
                    </Label>
                    <div className="relative">
                      <input
                        {...field}
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/jpeg,image/jpg,image/png,image/webp,.pdf,.doc,.docx"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            onChange(file)
                          }
                        }}
                      />
                      <label
                        htmlFor={field.name}
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex items-center justify-center gap-3 h-24 border-2 border-dashed rounded-xl transition-all cursor-pointer group ${error
                          ? 'border-red-300 bg-red-50/50'
                          : 'border-slate-300 hover:border-[#003DA5] hover:bg-blue-50/50'
                          }`}
                      >
                        <Upload className={`w-6 h-6 transition-colors ${error
                          ? 'text-red-500'
                          : 'text-muted-foreground group-hover:text-[#003DA5]'
                          }`} />
                        <div className="text-center">
                          <p className={`text-sm font-medium transition-colors ${error
                            ? 'text-red-600'
                            : 'text-foreground group-hover:text-[#003DA5]'
                            }`}>
                            Click to upload
                          </p>
                          <p className="text-xs text-muted-foreground">
                            PNG, JPG, WEBP, PDF, DOC up to 10MB
                          </p>
                        </div>
                      </label>
                    </div>

                    {error && (
                      <p className="text-xs text-red-600 mt-1">
                        {error.message}
                      </p>
                    )}

                    {value && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#003DA5]" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                              {value.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {(value.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            onChange(undefined)
                            if (fileInputRef.current) {
                              fileInputRef.current.value = ""
                            }
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </motion.div>
                    )}
                  </>
                )}
              />
            </motion.div>

            {/* Error Message */}
            {submitError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border-2 border-red-200 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </motion.div>
            )}

            {/* Info Box */}
            <motion.div variants={item}>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-orange-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">
                      Need Immediate Assistance?
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      For urgent issues, please contact our 24/7 support team directly
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={`tel:${CONTACT.phone.primary}`}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-[#003DA5] hover:text-blue-700 transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        {CONTACT.phone.primary}
                      </a>
                      <a
                        href={`https://wa.me/${CONTACT.phone.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold text-green-600 hover:text-green-700 transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        WhatsApp Support
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div variants={item}>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="w-full bg-gradient-to-r from-[#003DA5] to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold h-14 text-base shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting Complaint...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Complaint
                  </>
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground mt-3">
                By submitting, you agree to our complaint handling policy
              </p>
            </motion.div>
          </motion.form>
        )}
      </div>
    </Card>
  )
}
